import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TestEmailButton } from './TestEmailButton';
import { useAuth } from '@/hooks/useAuth';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { 
  Users, 
  UserPlus, 
  Shield, 
  Ban, 
  UserCheck, 
  Eye, 
  Edit, 
  Trash2, 
  MoreHorizontal,
  Activity,
  Mail,
  Phone,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Crown,
  Settings,
  Key,
  UserX,
  RefreshCw,
  Download,
  Upload,
  Filter,
  Search,
  LogIn,
  Send
} from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { useImpersonation } from "@/hooks/useImpersonation";
import { usePersistentTabState } from '@/hooks/usePersistentTabState';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { UserFilters } from './UserFilters';
import { useAdminPresence } from '@/hooks/useAdminPresence';
import { useUserPresence } from '@/hooks/useUserPresence';
import { OnlineAdminsIndicator, OnlineIndicator } from './OnlineAdminsIndicator';
import { SystemRole, SYSTEM_ROLE_LABELS, SYSTEM_ROLE_COLORS } from '@/types/roles';

interface UsersManagerProps {
  onRefresh: () => void;
  user: any;
}

interface User {
  id: string;
  full_name: string | null;
  email?: string | null;
  phone: string | null;
  avatar_url: string | null;
  system_role: 'client' | 'vendor' | 'relay' | 'admin' | 'hr' | 'super_admin';
  user_role: 'admin' | 'moderator' | 'hr' | 'manager' | 'relay_operator' | 'employee' | null;
  vendor_status: 'pending' | 'approved' | 'rejected' | null;
  created_at: string;
  updated_at: string;
  is_suspended?: boolean;
  last_login?: string;
  orders_count?: number;
  products_count?: number;
  // Nouvelles préférences utilisateur
  preferred_language?: 'fr' | 'en' | 'lg-fr';
  timezone?: string;
  theme?: 'light' | 'dark' | 'system';
  email_notifications?: boolean;
  sms_notifications?: boolean;
  two_factor_enabled?: boolean;
  account_status?: 'active' | 'inactive' | 'suspended' | 'pending_verification';
  notes?: string;
}

interface ActivityLog {
  id: string;
  actor_id: string;
  action: string;
  entity_type?: string;
  entity_id?: string;
  description?: string;
  created_at: string;
  meta: any;
  actor?: { full_name: string | null };
}

export const UsersManager = ({ onRefresh, user }: UsersManagerProps) => {
  const { profile } = useAuth(); // Récupérer le profil complet
  const { toast } = useToast();
  const { t } = useLanguage();
  const { startImpersonation } = useImpersonation();
  const { onlineAdmins, isOnline: isAdminOnline } = useAdminPresence(); // Hook pour la présence admin
  const { isOnline: isUserOnline } = useUserPresence(); // Hook pour la présence de tous les utilisateurs
  const [activeTab, setActiveTab] = usePersistentTabState(
    'admin-users-manager:activeTab',
    'users',
    ['users', 'roles', 'activity', 'bulk']
  );
  const [userDialogTab, setUserDialogTab] = usePersistentTabState(
    'admin-users-manager:userDialogTab',
    'general',
    ['general', 'preferences', 'security', 'notifications']
  );
  
  // State
  const [users, setUsers] = useState<User[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Dialog states
  const [userDialog, setUserDialog] = useState(false);
  const [activityDialog, setActivityDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [userDetailsDialog, setUserDetailsDialog] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  
  // Form states
  const [userForm, setUserForm] = useState({
    full_name: '',
    phone: '',
    email: '',
    password: '',
    role: 'client' as any,
    user_role: 'employee' as any,
    is_suspended: false,
    preferred_language: 'fr' as 'fr' | 'en' | 'lg-fr',
    timezone: 'Africa/Kinshasa',
    theme: 'system' as 'light' | 'dark' | 'system',
    email_notifications: true,
    sms_notifications: false,
    two_factor_enabled: false,
    account_status: 'active' as 'active' | 'inactive' | 'suspended' | 'pending_verification',
    notes: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, statusFilter, sortBy, sortOrder]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchUsers(),
        fetchActivityLogs()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching profiles:', error);
        throw error;
      }

      // Fetch additional user stats and roles from user_roles table
      const usersWithStats = await Promise.all(
        (data || []).map(async (user) => {
          const [ordersRes, productsRes, userRolesRes] = await Promise.all([
            supabase.from('orders').select('id').eq('client_id', user.id),
            supabase.from('products').select('id').eq('vendor_id', user.id),
            // Note: user_roles table no longer used after migration
            Promise.resolve({ data: [], error: null })
          ]);

          // Try to get last sign in from auth.users if we have admin access
          let lastSignIn = null;
          // Disabled auth.admin API call as it requires service_role_key
          // and we're using client-side authentication
          lastSignIn = null;
          
          /* try {
            const { data: authUser } = await supabase.auth.admin.getUserById(user.id);
            lastSignIn = authUser?.user?.last_sign_in_at || null;
          } catch (error) {
            // Silently fail if we don't have admin access
            console.debug('Could not fetch auth data:', error);
          } */

          // Get the highest priority role from user_roles table
          const userRoles = userRolesRes.data || [];
          const rolePriority = { super_admin: 1, admin: 2, hr: 3, vendor: 4, relay: 5, client: 6 };
          const highestRole = userRoles.length > 0 
            ? userRoles.reduce((highest, curr) => {
                const currentPriority = rolePriority[curr.role as keyof typeof rolePriority] || 999;
                const highestPriority = rolePriority[highest.role as keyof typeof rolePriority] || 999;
                return currentPriority < highestPriority ? curr : highest;
              }).role
            : user.system_role; // Fallback to profile system_role if no role in user_roles

          return {
            ...user,
            role: highestRole, // Override with role from user_roles
            email: user.email, // Email now comes directly from profiles table
            orders_count: ordersRes.data?.length || 0,
            products_count: productsRes.data?.length || 0,
            is_suspended: user.account_status === 'suspended',
            last_login: lastSignIn,
            preferred_language: (user.preferred_language as "fr" | "en" | "lg-fr") || "fr",
            theme: (user.theme as "light" | "dark" | "system") || "system",
            account_status: (user.account_status as "active" | "inactive" | "suspended" | "pending_verification") || "active"
          };
        })
      );

      // Filter users based on super admin status
      const isSuperAdmin = profile?.system_role === 'super_admin';
      const filteredUsers = isSuperAdmin 
        ? usersWithStats 
        : usersWithStats.filter(u => u.system_role !== 'super_admin');

      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      // Récupérer les logs sans jointure (la FK n'existe pas)
      const { data: logsData, error: logsError } = await supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (logsError) throw logsError;
      
      // Récupérer les noms des acteurs séparément
      if (logsData && logsData.length > 0) {
        const actorIds = [...new Set(logsData.map(l => l.actor_id).filter(Boolean))];
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', actorIds);
        
        const profilesMap = new Map(profilesData?.map(p => [p.id, p.full_name]) || []);
        
        const logsWithActors = logsData.map(log => ({
          ...log,
          actor: { full_name: profilesMap.get(log.actor_id) || null }
        }));
        
        setActivityLogs(logsWithActors);
      } else {
        setActivityLogs([]);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.full_name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.phone?.includes(searchTerm)
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.system_role === roleFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'suspended') {
        filtered = filtered.filter(user => user.is_suspended);
      } else if (statusFilter === 'active') {
        filtered = filtered.filter(user => !user.is_suspended);
      }
    }

    // Sorting
    filtered.sort((a, b) => {
      let compareResult = 0;
      
      switch (sortBy) {
        case 'created_at':
          compareResult = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'full_name':
          compareResult = (a.full_name || '').localeCompare(b.full_name || '');
          break;
        case 'email':
          compareResult = (a.email || '').localeCompare(b.email || '');
          break;
        case 'role':
          compareResult = a.system_role.localeCompare(b.system_role);
          break;
        default:
          compareResult = 0;
      }
      
      return sortOrder === 'asc' ? compareResult : -compareResult;
    });

    setFilteredUsers(filtered);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
    setSortBy('created_at');
    setSortOrder('desc');
  };

  const handleToggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleImpersonate = async (targetUser: User) => {
    try {
      if (targetUser.id === profile?.id) {
        toast({
          title: "Erreur",
          description: "Vous ne pouvez pas vous impersoner vous-même",
          variant: "destructive",
        });
        return;
      }

      if (targetUser.system_role === 'super_admin') {
        toast({
          title: "Erreur",
          description: "Vous ne pouvez pas impersoner un super admin",
          variant: "destructive",
        });
        return;
      }

      await startImpersonation(targetUser.id, targetUser.email || targetUser.full_name || 'Utilisateur');
      
      toast({
        title: "Impersonation démarrée",
        description: `Vous êtes maintenant connecté en tant que ${targetUser.full_name || targetUser.email}`,
      });
    } catch (error) {
      console.error('Erreur lors de l\'impersonation:', error);
      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : "Impossible de démarrer l'impersonation",
        variant: "destructive",
      });
    }
  };

  const handleUserAction = async (userId: string, action: string, data?: any) => {
    try {
      let message = '';

      switch (action) {
        case 'suspend':
          await supabase
            .from('profiles')
            .update({ account_status: 'suspended', is_suspended: true })
            .eq('id', userId);
          message = 'Utilisateur suspendu';
          break;
        case 'activate':
          await supabase
            .from('profiles')
            .update({ account_status: 'active', is_suspended: false })
            .eq('id', userId);
          message = 'Utilisateur réactivé';
          break;
        case 'promote_admin':
          // Check if admin role already exists
          const { data: existingRole } = await supabase
            .from('user_roles')
            .select('id')
            .eq('user_id', userId)
            .eq('role', 'admin')
            .single();
          
          if (!existingRole) {
            await supabase
              .from('user_roles')
              .insert({ user_id: userId, role: 'admin', assigned_by: user?.id });
          }
          message = 'Utilisateur promu administrateur';
          break;
        case 'demote_client':
          // Remove admin and other elevated roles, keep only client
          await supabase
            .from('user_roles')
            .delete()
            .eq('user_id', userId)
            .in('role', ['admin', 'super_admin', 'hr', 'vendor', 'relay']);
          
          // Ensure client role exists
          const { data: clientRole } = await supabase
            .from('user_roles')
            .select('id')
            .eq('user_id', userId)
            .eq('role', 'client')
            .single();
          
          if (!clientRole) {
            await supabase
              .from('user_roles')
              .insert({ user_id: userId, role: 'client', assigned_by: user?.id });
          }
          message = 'Utilisateur rétrogradé en client';
          break;
        case 'update_role':
          // Update user_role in profiles for organizational roles
          await supabase
            .from('profiles')
            .update({ user_role: data.user_role })
            .eq('id', userId);
          message = 'Rôle organisationnel mis à jour';
          break;
        case 'delete':
          // Call edge function to delete user securely
          const { error: deleteError } = await supabase.functions.invoke('delete-user', {
            body: { userId }
          });
          if (deleteError) throw deleteError;
          message = 'Utilisateur supprimé avec succès';
          break;
        default:
          return;
      }
      
      toast({ title: message });
      await fetchUsers();
      onRefresh();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const checkAndFixEmployeeData = async () => {
    try {
      const { data: usersWithEmployeeRole, error: queryError } = await supabase
        .from('profiles')
        .select('id, full_name, user_role, employee_id')
        .eq('user_role', 'employee')
        .is('employee_id', null);

      if (queryError) throw queryError;

      if (usersWithEmployeeRole && usersWithEmployeeRole.length > 0) {
        const message = `${usersWithEmployeeRole.length} utilisateur(s) avec le rôle "employee" n'ont pas d'entrée dans la table employés. Voulez-vous créer automatiquement ces entrées ?`;
        
        if (confirm(message)) {
          let successCount = 0;
          for (const user of usersWithEmployeeRole) {
            const nameParts = (user.full_name || '').split(' ');
            const firstName = nameParts[0] || 'Prénom';
            const lastName = nameParts.slice(1).join(' ') || 'Nom';
            
            // Générer un code employé unique
            const { data: generatedCode } = await supabase.rpc('generate_employee_code');
            const employeeCode = generatedCode || `EMP${Date.now()}`;
            
            // Utiliser seulement les colonnes obligatoires (employee_code, first_name, last_name, position)
            const { data: newEmployee, error: employeeError } = await supabase
              .from('employees')
              .insert({
                employee_code: employeeCode,
                first_name: firstName,
                last_name: lastName,
                position: 'À définir'
              } as any)
              .select()
              .single();

            if (employeeError) {
              console.error('Error creating employee:', employeeError);
              continue;
            }

            if (newEmployee) {
              // Mettre à jour le lien dans profiles
              await supabase
                .from('profiles')
                .update({ employee_id: newEmployee.id })
                .eq('id', user.id);
                
              successCount++;
            }
          }

          toast({
            title: "Entrées créées",
            description: `${successCount} entrée(s) employé créée(s) avec succès.`,
          });

          await fetchAllData();
        }
      } else {
        toast({
          title: "Aucun problème détecté",
          description: "Tous les utilisateurs avec le rôle 'employee' ont une entrée dans la table employés.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Fonction pour renvoyer l'email de confirmation
  const handleResendConfirmationEmail = async (userEmail: string, userName: string) => {
    try {
      // Appeler l'API Supabase Admin pour renvoyer l'email
      const { error } = await supabase.functions.invoke('resend-confirmation-email', {
        body: { email: userEmail }
      });

      if (error) throw error;

      toast({
        title: "Email envoyé",
        description: `Un email de confirmation a été renvoyé à ${userName} (${userEmail})`,
      });
    } catch (error: any) {
      console.error('Erreur renvoi email:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de renvoyer l'email de confirmation",
        variant: "destructive"
      });
    }
  };

  const handleUserEdit = (user: User) => {
    setUserForm({
      full_name: user.full_name || '',
      phone: user.phone || '',
      email: user.email || '', // Email modifiable pour tous les utilisateurs
      password: '', // Pas de mot de passe à modifier
      role: user.system_role,
      user_role: user.user_role || 'employee',
      is_suspended: user.is_suspended || false,
      preferred_language: user.preferred_language || 'fr',
      timezone: user.timezone || 'Africa/Kinshasa',
      theme: user.theme || 'system',
      email_notifications: user.email_notifications !== false,
      sms_notifications: user.sms_notifications || false,
      two_factor_enabled: user.two_factor_enabled || false,
      account_status: user.account_status || 'active',
      notes: user.notes || ''
    });
    setSelectedUser(user);
    setIsCreatingUser(false);
    setUserDialog(true);
  };

  const handleNewUser = () => {
    setUserForm({
      full_name: '',
      phone: '',
      email: '',
      password: '',
      role: 'client',
      user_role: 'employee',
      is_suspended: false,
      preferred_language: 'fr',
      timezone: 'Africa/Kinshasa',
      theme: 'system',
      email_notifications: true,
      sms_notifications: false,
      two_factor_enabled: false,
      account_status: 'active',
      notes: ''
    });
    setSelectedUser(null);
    setIsCreatingUser(true);
    setUserDialog(true);
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isCreatingUser) {
        // Validation pour la création
        if (!userForm.email || !userForm.password) {
          toast({
            title: "Erreur",
            description: "L'email et le mot de passe sont requis pour créer un utilisateur",
            variant: "destructive"
          });
          return;
        }

        if (userForm.password.length < 6) {
          toast({
            title: "Erreur",
            description: "Le mot de passe doit contenir au moins 6 caractères",
            variant: "destructive"
          });
          return;
        }

        // SOLUTION ANTI-429: Utiliser la fonction admin au lieu de l'API directe
        console.log('🔧 Création utilisateur via fonction admin pour éviter 429...');
        
        const { data: adminResult, error: adminError } = await supabase.functions.invoke('admin-create-user', {
          body: {
            email: userForm.email,
            password: userForm.password,
            full_name: userForm.full_name,
            phone: userForm.phone,
            role: userForm.role,
            user_role: userForm.user_role,
            preferred_language: userForm.preferred_language,
            timezone: userForm.timezone,
            theme: userForm.theme,
            email_notifications: userForm.email_notifications,
            sms_notifications: userForm.sms_notifications,
            two_factor_enabled: userForm.two_factor_enabled,
            account_status: userForm.account_status,
            notes: userForm.notes
          }
        });

        if (adminError) {
          console.error('❌ Erreur fonction admin:', adminError);
          throw new Error(adminError.message || 'Erreur lors de la création via fonction admin');
        }

        if (!adminResult?.success) {
          console.error('❌ Fonction admin a échoué:', adminResult);
          throw new Error(adminResult?.error || 'La fonction admin a retourné une erreur');
        }

        console.log('✅ Utilisateur créé via fonction admin:', adminResult.user);
        
        toast({
          title: "Utilisateur créé avec succès",
          description: `${adminResult.user.email} a été créé et configuré`,
          variant: "default"
        });

        console.log('✅ Utilisateur créé via fonction admin:', adminResult.user);
        
        toast({
          title: "Utilisateur créé avec succès",
          description: `${adminResult.user.email} a été créé et configuré`,
          variant: "default"
        });
        
      } else {
        // Mise à jour d'un utilisateur existant
        if (!selectedUser) return;

        // Update system role in user_roles table
        // First, check if role has changed
        if (userForm.role !== selectedUser.system_role) {
          // Delete existing system roles
          await supabase
            .from('user_roles')
            .delete()
            .eq('user_id', selectedUser.id);
          
          // Insert the new role
          await supabase
            .from('user_roles')
            .insert({ 
              user_id: selectedUser.id, 
              role: userForm.role as any,
              assigned_by: user?.id 
            });
        }

        // Update profile information including system_role
        const { error } = await supabase
          .from('profiles')
          .update({
            full_name: userForm.full_name,
            phone: userForm.phone,
            system_role: userForm.role,
            user_role: userForm.user_role,
            preferred_language: userForm.preferred_language,
            timezone: userForm.timezone,
            theme: userForm.theme,
            email_notifications: userForm.email_notifications,
            sms_notifications: userForm.sms_notifications,
            two_factor_enabled: userForm.two_factor_enabled,
            account_status: userForm.account_status,
            notes: userForm.notes
          })
          .eq('id', selectedUser.id);

        if (error) throw error;
        
        toast({ title: "Utilisateur mis à jour avec succès" });
      }

      await fetchUsers();
      onRefresh();
      setUserDialog(false);
      setIsCreatingUser(false);
    } catch (error: any) {
      console.error('Erreur création/modification utilisateur:', error);
      
      let errorMessage = error.message;
      let errorTitle = "Erreur";
      
      // Messages d'erreur personnalisés
      if (error.message.includes('429') || error.message.includes('Too Many Requests')) {
        errorTitle = "Limite de taux dépassée";
        errorMessage = "Trop de tentatives. Veuillez attendre quelques minutes avant de réessayer.";
      } else if (error.message.includes('User already registered')) {
        errorTitle = "Utilisateur existant";
        errorMessage = "Un compte avec cet email existe déjà.";
      } else if (error.message.includes('Invalid email')) {
        errorTitle = "Email invalide";
        errorMessage = "L'adresse email fournie n'est pas valide.";
      } else if (error.message.includes('Password should be at least 6 characters')) {
        errorTitle = "Mot de passe trop court";
        errorMessage = "Le mot de passe doit contenir au moins 6 caractères.";
      } else if (error.message.includes('email_notifications')) {
        errorTitle = "Erreur de base de données";
        errorMessage = "Problème de structure de base de données. Contactez l'administrateur.";
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const handleBulkAction = async (action: 'activate' | 'suspend' | 'email' | 'reset_password') => {
    if (selectedUsers.length === 0) {
      toast({
        title: "Aucun utilisateur sélectionné",
        description: "Veuillez sélectionner au moins un utilisateur",
        variant: "destructive"
      });
      return;
    }

    try {
      switch (action) {
        case 'activate':
          await Promise.all(
            selectedUsers.map(userId =>
              supabase
                .from('profiles')
                .update({ account_status: 'active', is_suspended: false })
                .eq('id', userId)
            )
          );
          toast({ title: `${selectedUsers.length} utilisateur(s) activé(s)` });
          break;
        
        case 'suspend':
          await Promise.all(
            selectedUsers.map(userId =>
              supabase
                .from('profiles')
                .update({ account_status: 'suspended', is_suspended: true })
                .eq('id', userId)
            )
          );
          toast({ title: `${selectedUsers.length} utilisateur(s) suspendu(s)` });
          break;
        
        case 'email':
          toast({ 
            title: "Fonctionnalité à venir",
            description: "L'envoi d'emails groupés sera bientôt disponible"
          });
          break;
        
        case 'reset_password':
          toast({ 
            title: "Fonctionnalité à venir",
            description: "La réinitialisation groupée de mots de passe sera bientôt disponible"
          });
          break;
      }
      
      setSelectedUsers([]);
      await fetchUsers();
      onRefresh();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleExportUsers = () => {
    const csv = [
      ['Nom', 'Téléphone', 'Rôle', 'Statut', 'Date création'].join(','),
      ...filteredUsers.map(user => [
        user.full_name || '',
        user.phone || '',
        user.role,
        user.is_suspended ? 'Suspendu' : 'Actif',
        new Date(user.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `utilisateurs_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast({ title: "Export réussi" });
  };

  const handleExportActivityReport = () => {
    const csv = [
      ['Utilisateur', 'Action', 'Date'].join(','),
      ...activityLogs.map(log => [
        log.actor?.full_name || 'Inconnu',
        log.action,
        new Date(log.created_at).toLocaleString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `activite_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast({ title: "Export réussi" });
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  const getRoleBadge = (role: string, userRole?: string | null) => {
    const roleColors = {
      super_admin: 'bg-orange-100 text-orange-800 border-orange-300',
      admin: 'bg-red-100 text-red-800 border-red-300',
      hr: 'bg-purple-100 text-purple-800 border-purple-300',
      vendor: 'bg-blue-100 text-blue-800 border-blue-300',
      relay: 'bg-green-100 text-green-800 border-green-300',
      client: 'bg-gray-100 text-gray-800 border-gray-300'
    };

    const roleIcons = {
      super_admin: Crown,
      admin: Shield,
      hr: Users,
      vendor: Users,
      relay: Settings,
      client: UserCheck
    };

    const Icon = roleIcons[role as keyof typeof roleIcons] || UserCheck;

    return (
      <div className="flex flex-col space-y-1">
        <Badge variant="outline" className={roleColors[role as keyof typeof roleColors]}>
          <Icon className="h-3 w-3 mr-1" />
          {role === 'super_admin' ? 'Super Admin' : role.charAt(0).toUpperCase() + role.slice(1)}
        </Badge>
        {userRole && (
          <Badge variant="outline" className="text-xs">
            {userRole}
          </Badge>
        )}
      </div>
    );
  };

  const getStatusBadge = (user: User) => {
    // Vérifier le statut de suspension en premier
    if (user.is_suspended || user.account_status === 'suspended') {
      return <Badge variant="destructive"><Ban className="h-3 w-3 mr-1" />{t('employee.suspended')}</Badge>;
    }
    // Vérifier si en attente de vérification email
    if (user.account_status === 'pending_verification') {
      return <Badge variant="outline" className="text-amber-600 border-amber-300"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
    }
    // Vérifier si inactif
    if (user.account_status === 'inactive') {
      return <Badge variant="secondary"><XCircle className="h-3 w-3 mr-1" />Inactif</Badge>;
    }
    // Par défaut: actif
    return <Badge variant="outline" className="text-green-600 border-green-300"><CheckCircle className="h-3 w-3 mr-1" />{t('employee.active')}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            {t('admin.employees.title') || 'Gestion des Utilisateurs'}
          </h2>
          <p className="text-muted-foreground mt-1">
            {t('admin.employees.subtitle') || 'Gérez les comptes utilisateurs et leurs permissions'}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <TestEmailButton />
          <Button variant="outline" onClick={checkAndFixEmployeeData}>
            <AlertTriangle className="h-4 w-4 mr-2" />
            Vérifier les employés
          </Button>
          <Button variant="outline" onClick={fetchAllData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Button 
            onClick={() => setImportDialog(true)}
            variant="outline"
          >
            <Upload className="h-4 w-4 mr-2" />
            Importer
          </Button>
          <Button onClick={handleNewUser}>
            <UserPlus className="h-4 w-4 mr-2" />
            {t('admin.users.new_user')}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 bg-white/80 backdrop-blur-sm shadow-lg rounded-xl p-1">
          <TabsTrigger value="users" className="flex items-center space-x-2">
            <Users className="h-4 w-4" />
            <span>{t('admin.tabs.users') || 'Utilisateurs'}</span>
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center space-x-2">
            <Shield className="h-4 w-4" />
            <span>{t('admin.tabs.roles') || 'Rôles'}</span>
          </TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center space-x-2">
            <Activity className="h-4 w-4" />
            <span>{t('admin.tabs.activity') || 'Activité'}</span>
          </TabsTrigger>
          <TabsTrigger value="bulk" className="flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>{t('admin.tabs.bulk') || 'Actions groupées'}</span>
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-500 rounded-xl">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('employee.total_users')}</p>
                    <p className="text-2xl font-bold">{users.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-green-500 rounded-xl">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('employee.active')}</p>
                    <p className="text-2xl font-bold">{users.filter(u => !u.is_suspended).length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-red-500 rounded-xl">
                    <Ban className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('employee.suspended')}</p>
                    <p className="text-2xl font-bold">{users.filter(u => u.is_suspended).length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-purple-500 rounded-xl">
                    <Crown className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('employee.administrators')}</p>
                    <p className="text-2xl font-bold">{users.filter(u => u.role === 'admin' || u.role === 'super_admin').length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Admins connectés */}
          <OnlineAdminsIndicator />

          {/* Filters */}
          <UserFilters
            roleFilter={roleFilter}
            searchQuery={searchTerm}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRoleChange={setRoleFilter}
            onSearchChange={setSearchTerm}
            onSortByChange={setSortBy}
            onSortOrderToggle={handleToggleSortOrder}
            onClearFilters={handleClearFilters}
          />

          {/* Users Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Users className="h-5 w-5" />
                <span>{t('employee.user_list')} ({filteredUsers.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
               <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </TableHead>
                    <TableHead>{t('employee.user')}</TableHead>
                    <TableHead>{t('employee.role')}</TableHead>
                    <TableHead>{t('employee.preferences')}</TableHead>
                    <TableHead>{t('employee.status')}</TableHead>
                    <TableHead>{t('employee.statistics')}</TableHead>
                    <TableHead>{t('employee.last_connection')}</TableHead>
                    <TableHead>{t('employee.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="rounded"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="relative">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                              <Users className="h-5 w-5 text-primary" />
                            </div>
                            {/* Indicateur en ligne pour tous les utilisateurs */}
                            {(isUserOnline(user.id) || ((user.system_role === 'admin' || user.system_role === 'super_admin') && isAdminOnline(user.id))) && (
                              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{user.full_name || t('employee.no_name')}</p>
                              <OnlineIndicator isOnline={isUserOnline(user.id) || ((user.system_role === 'admin' || user.system_role === 'super_admin') && isAdminOnline(user.id))} />
                            </div>
                            <p className="text-sm text-muted-foreground">{user.phone || t('employee.no_phone')}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getRoleBadge(user.system_role, user.user_role)}</TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          <div className="flex items-center space-x-1">
                            <span>🌐</span>
                            <span>{user.preferred_language === 'fr' ? '🇫🇷 Français' : user.preferred_language === 'en' ? '🇬🇧 English' : user.preferred_language === 'lg-fr' ? '🇨🇩 Lingala-FR' : '🇫🇷 Français'}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span className="text-xs text-muted-foreground">
                              {user.timezone === 'Africa/Kinshasa' ? 'Kinshasa' : user.timezone === 'Europe/Paris' ? 'Paris' : user.timezone || 'Kinshasa'}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(user)}</TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {user.system_role === 'client' && <p>{user.orders_count} {t('employee.commands')}</p>}
                          {user.vendor_status === 'approved' && <p>{user.products_count} {t('vendor.products')}</p>}
                          {(user.system_role === 'admin' || user.system_role === 'super_admin') && <p>{t('employee.administrators')}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {user.last_login ? new Date(user.last_login).toLocaleDateString('fr-FR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'Jamais'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => handleUserEdit(user)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedUser(user);
                              setUserDetailsDialog(true);
                            }}>
                              <Eye className="h-4 w-4 mr-2" />
                              Voir détails
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedUser(user);
                              setEmailRecipient(user.email || '');
                              setEmailSubject('');
                              setEmailMessage('');
                              setEmailDialog(true);
                            }}>
                              <Mail className="h-4 w-4 mr-2" />
                              Envoyer un email
                            </DropdownMenuItem>
                            {/* Bouton de renvoi d'email de confirmation - seulement pour les comptes en attente */}
                            {user.account_status === 'pending_verification' && user.email && (
                              <DropdownMenuItem 
                                onClick={() => handleResendConfirmationEmail(user.email!, user.full_name || 'Utilisateur')}
                                className="text-amber-600"
                              >
                                <Send className="h-4 w-4 mr-2" />
                                Renvoyer email de confirmation
                              </DropdownMenuItem>
                            )}
                            {/* Bouton d'impersonation - super_admin peut impersoner tout le monde sauf super_admin, admin peut impersoner seulement clients/vendeurs */}
                            {(profile?.system_role === 'super_admin' || profile?.system_role === 'admin') && 
                             user.id !== profile?.id && 
                             user.system_role !== 'super_admin' && 
                             !(profile?.system_role === 'admin' && user.system_role === 'admin') && (
                              <DropdownMenuItem 
                                onClick={() => handleImpersonate(user)}
                                className="text-blue-600"
                              >
                                <LogIn className="h-4 w-4 mr-2" />
                                Se connecter en tant que
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {/* Ne pas permettre la suspension des super admins */}
                            {user.is_suspended ? (
                              <DropdownMenuItem 
                                onClick={() => handleUserAction(user.id, 'activate')}
                                className="text-green-600"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Réactiver
                              </DropdownMenuItem>
                            ) : user.role !== 'super_admin' && (
                              <DropdownMenuItem 
                                onClick={() => handleUserAction(user.id, 'suspend')}
                                className="text-red-600"
                              >
                                <Ban className="h-4 w-4 mr-2" />
                                Suspendre
                              </DropdownMenuItem>
                            )}
                            {/* Ne pas afficher "Promouvoir admin" si l'utilisateur est déjà admin ou super_admin */}
                            {user.role !== 'admin' && user.role !== 'super_admin' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleUserAction(user.id, 'promote_admin')}
                                  className="text-purple-600"
                                >
                                  <Crown className="h-4 w-4 mr-2" />
                                  Promouvoir admin
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem>
                              <Key className="h-4 w-4 mr-2" />
                              Réinitialiser MDP
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                if (confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.full_name} ? Cette action est irréversible.`)) {
                                  handleUserAction(user.id, 'delete');
                                }
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Rôles système</CardTitle>
                <CardDescription>
                  Gérez les permissions et hiérarchies des rôles
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { role: 'admin', name: 'Administrateur', description: 'Accès complet au système', users: users.filter(u => u.system_role === 'admin' || u.system_role === 'super_admin').length },
                  { role: 'vendor', name: 'Vendeur', description: 'Gestion des produits et commandes', users: users.filter(u => u.vendor_status === 'approved').length },
                  { role: 'relay', name: 'Point relais', description: 'Gestion des retraits et stock', users: users.filter(u => u.system_role === 'relay').length },
                  { role: 'client', name: 'Client', description: 'Utilisateur standard', users: users.filter(u => u.system_role === 'client').length }
                ].map((role) => (
                  <div key={role.role} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getRoleBadge(role.role)}
                      <div>
                        <p className="font-medium">{role.name}</p>
                        <p className="text-sm text-muted-foreground">{role.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{role.users}</p>
                      <p className="text-sm text-muted-foreground">utilisateurs</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Rôles métier</CardTitle>
                <CardDescription>
                  Rôles organisationnels pour la gestion interne
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { role: 'hr', name: 'Ressources Humaines', users: users.filter(u => u.user_role === 'hr').length },
                  { role: 'manager', name: 'Manager', users: users.filter(u => u.user_role === 'manager').length },
                  { role: 'employee', name: 'Employé', users: users.filter(u => u.user_role === 'employee').length },
                  { role: 'moderator', name: 'Modérateur', users: users.filter(u => u.user_role === 'moderator').length }
                ].map((role) => (
                  <div key={role.role} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">{role.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{role.users}</p>
                      <p className="text-sm text-muted-foreground">utilisateurs</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Activity className="h-5 w-5" />
                <span>Journal d'activité</span>
              </CardTitle>
              <CardDescription>
                Historique des actions effectuées par les utilisateurs ({activityLogs.length} entrées)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {activityLogs.map((log) => {
                  // Icône basée sur le type d'action
                  const getActionIcon = (action: string) => {
                    if (action.includes('create') || action.includes('user_created')) return '➕';
                    if (action.includes('delete') || action.includes('remove')) return '🗑️';
                    if (action.includes('update') || action.includes('role_changed')) return '✏️';
                    if (action.includes('login') || action.includes('auth')) return '🔐';
                    if (action.includes('storage') || action.includes('backup')) return '💾';
                    if (action.includes('system') || action.includes('initialized')) return '⚙️';
                    return '📋';
                  };
                  
                  // Description lisible
                  const getActionDescription = (action: string, description?: string) => {
                    if (description) return description;
                    const actionMap: Record<string, string> = {
                      'user_created': 'Nouvel utilisateur créé',
                      'role_changed': 'Rôle utilisateur modifié',
                      'storage_backup_created': 'Sauvegarde de stockage créée',
                      'system_initialized': 'Système initialisé',
                      'profile_updated': 'Profil mis à jour',
                      'order_created': 'Commande créée',
                      'product_created': 'Produit créé',
                      'vendor_approved': 'Vendeur approuvé'
                    };
                    return actionMap[action] || action.replace(/_/g, ' ');
                  };
                  
                  return (
                    <div key={log.id} className="flex items-center space-x-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-lg">
                        {getActionIcon(log.action)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {log.actor?.full_name || 'Système'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {getActionDescription(log.action, log.description)}
                        </p>
                        {log.entity_type && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Type: {log.entity_type}
                          </p>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground text-right shrink-0">
                        {new Date(log.created_at).toLocaleString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  );
                })}
                {activityLogs.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium mb-1">Aucune activité enregistrée</p>
                    <p className="text-sm">Les actions des utilisateurs apparaîtront ici une fois qu'ils effectueront des opérations dans le système</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bulk Actions Tab */}
        <TabsContent value="bulk" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Actions groupées</CardTitle>
                <CardDescription>
                  Effectuez des actions sur plusieurs utilisateurs à la fois
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedUsers.length > 0 ? (
                  <div className="mb-4 p-3 bg-primary/10 rounded-lg">
                    <p className="text-sm font-medium">
                      {selectedUsers.length} utilisateur(s) sélectionné(s)
                    </p>
                  </div>
                ) : (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      Sélectionnez des utilisateurs dans l'onglet "Utilisateurs" pour activer les actions groupées
                    </p>
                  </div>
                )}
                <Button 
                  className="w-full justify-start"
                  onClick={() => handleBulkAction('activate')}
                  disabled={selectedUsers.length === 0}
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Activer les utilisateurs sélectionnés
                </Button>
                <Button 
                  variant="destructive" 
                  className="w-full justify-start"
                  onClick={() => handleBulkAction('suspend')}
                  disabled={selectedUsers.length === 0}
                >
                  <Ban className="h-4 w-4 mr-2" />
                  Suspendre les utilisateurs sélectionnés
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => handleBulkAction('email')}
                  disabled={selectedUsers.length === 0}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Envoyer un email groupé
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => handleBulkAction('reset_password')}
                  disabled={selectedUsers.length === 0}
                >
                  <Key className="h-4 w-4 mr-2" />
                  Forcer le changement de mot de passe
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Import/Export</CardTitle>
                <CardDescription>
                  Gérez les données utilisateurs en masse
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleExportUsers}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exporter tous les utilisateurs
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => toast({ 
                    title: "Fonctionnalité à venir",
                    description: "L'import d'utilisateurs sera bientôt disponible"
                  })}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Importer des utilisateurs
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleExportActivityReport}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Rapport d'activité
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* User Edit Dialog */}
      <Dialog open={userDialog} onOpenChange={setUserDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isCreatingUser ? 'Créer un utilisateur' : 'Modifier l\'utilisateur'}
            </DialogTitle>
            <DialogDescription>
              {isCreatingUser 
                ? 'Créez un nouveau compte utilisateur avec ses préférences'
                : 'Modifiez les informations, permissions et préférences de l\'utilisateur'
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUserSubmit} className="space-y-6">
            <Tabs value={userDialogTab} onValueChange={setUserDialogTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="general">Général</TabsTrigger>
                <TabsTrigger value="preferences">Préférences</TabsTrigger>
                <TabsTrigger value="security">Sécurité</TabsTrigger>
                <TabsTrigger value="notifications">Notifications</TabsTrigger>
              </TabsList>

              {/* Onglet Général */}
              <TabsContent value="general" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Nom complet</Label>
                    <Input
                      id="full_name"
                      value={userForm.full_name}
                      onChange={(e) => setUserForm(prev => ({ ...prev, full_name: e.target.value }))}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      value={userForm.phone}
                      onChange={(e) => setUserForm(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </div>

                  {isCreatingUser && (
                    <div>
                      <Label htmlFor="password">Mot de passe</Label>
                      <Input
                        id="password"
                        type="password"
                        value={userForm.password}
                        onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                        minLength={6}
                        required
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Minimum 6 caractères requis
                      </p>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="role">Rôle système</Label>
                    <Select value={userForm.role} onValueChange={(value: any) => setUserForm(prev => ({ ...prev, role: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client">Client</SelectItem>
                        <SelectItem value="vendor">Vendeur</SelectItem>
                        <SelectItem value="relay">Point relais</SelectItem>
                        <SelectItem value="admin">Administrateur</SelectItem>
                        <SelectItem value="hr">Ressources humaines</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="user_role">Rôle organisationnel</Label>
                    <Select value={userForm.user_role} onValueChange={(value: any) => setUserForm(prev => ({ ...prev, user_role: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="moderator">Modérateur</SelectItem>
                        <SelectItem value="hr">RH</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="relay_operator">Opérateur relais</SelectItem>
                        <SelectItem value="employee">Employé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="account_status">Statut du compte</Label>
                    <Select value={userForm.account_status} onValueChange={(value: any) => setUserForm(prev => ({ ...prev, account_status: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Actif</SelectItem>
                        <SelectItem value="inactive">Inactif</SelectItem>
                        <SelectItem value="suspended">Suspendu</SelectItem>
                        <SelectItem value="pending_verification">En attente de vérification</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notes administratives</Label>
                  <textarea
                    id="notes"
                    className="w-full min-h-[80px] p-3 border border-input rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                    value={userForm.notes}
                    onChange={(e) => setUserForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Notes internes concernant cet utilisateur..."
                  />
                </div>
              </TabsContent>

              {/* Onglet Préférences */}
              <TabsContent value="preferences" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="preferred_language">Langue par défaut</Label>
                    <Select value={userForm.preferred_language} onValueChange={(value: any) => setUserForm(prev => ({ ...prev, preferred_language: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fr">🇫🇷 Français</SelectItem>
                        <SelectItem value="en">🇬🇧 English</SelectItem>
                        <SelectItem value="lg-fr">🇨🇩 Lingala-Français</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="timezone">Fuseau horaire</Label>
                    <Select value={userForm.timezone} onValueChange={(value: any) => setUserForm(prev => ({ ...prev, timezone: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Africa/Kinshasa">🇨🇩 Kinshasa (WAT)</SelectItem>
                        <SelectItem value="Europe/Paris">🇫🇷 Paris (CET)</SelectItem>
                        <SelectItem value="Europe/London">🇬🇧 Londres (GMT)</SelectItem>
                        <SelectItem value="America/New_York">🇺🇸 New York (EST)</SelectItem>
                        <SelectItem value="Asia/Tokyo">🇯🇵 Tokyo (JST)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="theme">Thème interface</Label>
                    <Select value={userForm.theme} onValueChange={(value: any) => setUserForm(prev => ({ ...prev, theme: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="system">🖥️ Système</SelectItem>
                        <SelectItem value="light">☀️ Clair</SelectItem>
                        <SelectItem value="dark">🌙 Sombre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>

              {/* Onglet Sécurité */}
              <TabsContent value="security" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="two_factor_enabled"
                      checked={userForm.two_factor_enabled}
                      onCheckedChange={(checked) => setUserForm(prev => ({ ...prev, two_factor_enabled: checked }))}
                    />
                    <Label htmlFor="two_factor_enabled" className="flex items-center space-x-2">
                      <Key className="h-4 w-4" />
                      <span>Authentification à deux facteurs</span>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_suspended"
                      checked={userForm.is_suspended}
                      onCheckedChange={(checked) => setUserForm(prev => ({ ...prev, is_suspended: checked }))}
                    />
                    <Label htmlFor="is_suspended" className="flex items-center space-x-2">
                      <Ban className="h-4 w-4" />
                      <span>Compte suspendu</span>
                    </Label>
                  </div>
                </div>
              </TabsContent>

              {/* Onglet Notifications */}
              <TabsContent value="notifications" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="email_notifications"
                      checked={userForm.email_notifications}
                      onCheckedChange={(checked) => setUserForm(prev => ({ ...prev, email_notifications: checked }))}
                    />
                    <Label htmlFor="email_notifications" className="flex items-center space-x-2">
                      <Mail className="h-4 w-4" />
                      <span>Notifications par email</span>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="sms_notifications"
                      checked={userForm.sms_notifications}
                      onCheckedChange={(checked) => setUserForm(prev => ({ ...prev, sms_notifications: checked }))}
                    />
                    <Label htmlFor="sms_notifications" className="flex items-center space-x-2">
                      <Phone className="h-4 w-4" />
                      <span>Notifications par SMS</span>
                    </Label>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUserDialog(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {isCreatingUser ? 'Créer l\'utilisateur' : 'Mettre à jour'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog open={userDetailsDialog} onOpenChange={setUserDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails de l'utilisateur</DialogTitle>
            <DialogDescription>
              Informations complètes sur {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Nom complet</Label>
                  <p className="font-medium">{selectedUser.full_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{selectedUser.email || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Téléphone</Label>
                  <p className="font-medium">{selectedUser.phone || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Rôle</Label>
                  <div className="mt-1">{getRoleBadge(selectedUser.role, selectedUser.user_role)}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Statut</Label>
                  <div className="mt-1">{getStatusBadge(selectedUser)}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Langue préférée</Label>
                  <p className="font-medium">
                    {selectedUser.preferred_language === 'fr' ? '🇫🇷 Français' : 
                     selectedUser.preferred_language === 'en' ? '🇬🇧 English' : 
                     selectedUser.preferred_language === 'lg-fr' ? '🇨🇩 Lingala-FR' : '🇫🇷 Français'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Fuseau horaire</Label>
                  <p className="font-medium">{selectedUser.timezone || 'Africa/Kinshasa'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Dernière connexion</Label>
                  <p className="font-medium">
                    {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Jamais'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Membre depuis</Label>
                  <p className="font-medium">{new Date(selectedUser.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <Label className="text-muted-foreground">Statistiques</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  {selectedUser.system_role === 'client' && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Commandes</p>
                      <p className="text-2xl font-bold text-blue-600">{selectedUser.orders_count || 0}</p>
                    </div>
                  )}
                  {selectedUser.vendor_status === 'approved' && (
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Produits</p>
                      <p className="text-2xl font-bold text-green-600">{selectedUser.products_count || 0}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label className="text-muted-foreground">Préférences de notification</Label>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4" />
                    <span className="text-sm">{selectedUser.email_notifications ? '✓ Email' : '✗ Email'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4" />
                    <span className="text-sm">{selectedUser.sms_notifications ? '✓ SMS' : '✗ SMS'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Key className="h-4 w-4" />
                    <span className="text-sm">{selectedUser.two_factor_enabled ? '✓ 2FA' : '✗ 2FA'}</span>
                  </div>
                </div>
              </div>
              
              {selectedUser.notes && (
                <>
                  <Separator />
                  <div>
                    <Label className="text-muted-foreground">Notes</Label>
                    <p className="mt-2 text-sm">{selectedUser.notes}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Envoyer un email</DialogTitle>
            <DialogDescription>
              Envoyer un email à {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email_recipient">Destinataire (Email)</Label>
              <Input
                id="email_recipient"
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="email@example.com"
                required
              />
              {!selectedUser?.email && (
                <p className="text-sm text-yellow-600 mt-1">
                  ⚠️ Aucun email trouvé pour cet utilisateur. Veuillez en saisir un.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="email_subject">Sujet</Label>
              <Input
                id="email_subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Sujet de l'email"
                required
              />
            </div>
            <div>
              <Label htmlFor="email_message">Message</Label>
              <Textarea
                id="email_message"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Votre message..."
                rows={6}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEmailDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={async () => {
                if (!emailRecipient || !emailSubject || !emailMessage) {
                  toast({
                    title: "Erreur",
                    description: "Veuillez remplir tous les champs",
                    variant: "destructive"
                  });
                  return;
                }

                setSendingEmail(true);
                try {
                  const { error } = await supabase.functions.invoke('send-admin-email', {
                    body: {
                      recipientEmail: emailRecipient,
                      recipientName: selectedUser?.full_name || 'Utilisateur',
                      subject: emailSubject,
                      message: emailMessage
                    }
                  });

                  if (error) throw error;

                  toast({
                    title: "Email envoyé",
                    description: `Email envoyé à ${emailRecipient}`
                  });
                  setEmailDialog(false);
                  setEmailRecipient('');
                  setEmailSubject('');
                  setEmailMessage('');
                } catch (error: any) {
                  console.error('Error sending email:', error);
                  toast({
                    title: "Erreur",
                    description: error.message || "Impossible d'envoyer l'email",
                    variant: "destructive"
                  });
                } finally {
                  setSendingEmail(false);
                }
              }}
              disabled={sendingEmail}
            >
              <Mail className="h-4 w-4 mr-2" />
              {sendingEmail ? 'Envoi en cours...' : 'Envoyer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Users Dialog */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importer des utilisateurs</DialogTitle>
            <DialogDescription>
              Importez des utilisateurs depuis un fichier CSV
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 font-medium mb-2">Format du fichier CSV:</p>
              <code className="text-xs bg-white p-2 block rounded">
                full_name,phone,email,role<br/>
                Jean Dupont,+243123456789,jean@example.com,client<br/>
                Marie Martin,+243987654321,marie@example.com,vendor
              </code>
            </div>
            <div>
              <Label htmlFor="import_file">Fichier CSV</Label>
              <Input
                id="import_file"
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setImportDialog(false)}>
              Annuler
            </Button>
            <Button onClick={() => {
              if (!importFile) {
                toast({
                  title: "Erreur",
                  description: "Veuillez sélectionner un fichier",
                  variant: "destructive"
                });
                return;
              }
              toast({
                title: "Import en cours",
                description: "Les utilisateurs sont en cours d'importation..."
              });
              setImportDialog(false);
            }}>
              <Upload className="h-4 w-4 mr-2" />
              Importer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};